<template>
    <div
        class="vld-parent noFocusOutline"
        ref="sampleListDiv"
        tabindex="-1"
        style="min-height: 100px"
    >
        <loading
            class="pt-16"
            v-model:active="isLoading"
            :is-full-page="false"
            loader="dots"
            color="#1976d2"
            backgroundColor="#f5f5f5"
        >
        </loading>
        <v-autocomplete
            v-model="activeSample"
            ref="autocomplete"
            auto-select-first
            clearable
            hint="You can also use a barcode scanner"
            :items="samplesArray"
            persistent-hint
            item-value="id"
            item-title="samp"
            label="Search for samples"
            prepend-icon="$test-tube"
            return-object
            @update:model-value="change"
        >
        </v-autocomplete>
        <ul v-if="activeSample" class="tw-mt-5 tw-ml-2">
            <li
                v-for="protocol in activeSample.protocols"
                :key="protocol.code"
                class="tw-capitalize tw-text-sm tw-text-blue-600"
            >
                <a :href="protocol.url" target="_blank" rel="noopener noreferrer">{{
                    protocol.code
                }}</a>
                - {{ protocol.name }}
            </li>
        </ul>
    </div>
</template>

<script>
import { defineComponent, nextTick } from "vue";
import onScan from "onscan.js";
import Loading from "vue-loading-overlay";
import { EventBus } from "@/lib/event-bus";
import { fetchSamplesByExperiment } from "@/services/samples/samplesAPI";
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { useToast } from "vue-toastification";

export default defineComponent({
    name: "SampleSelect",
    components: { Loading },
    props: ["experimentId"],
    computed: mapGetters("plateCreate", ["isMultiAssign", "protocols", "activeCondition"]),

    setup() {
        return {
            toast: useToast(),
        };
    },

    data() {
        return {
            activeSample: null,
            autoFocus: true,
            samplesArray: [],
            isLoading: true,
        };
    },

    mounted() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        onScan.attachTo(document, {
            onScan: function (sCode, iQty) {
                self.activeSample = _.find(self.samplesArray, { samp: sCode });
                if (!self.activeSample) {
                    self.toast.error("Sample not found");
                } else if (!self.isMultiAssign) {
                    // Open option menu if not isMultiAssign
                    self.setActiveCondition(self.activeSample);
                    this.$emit("openOptionsMenu", "scan");
                } else {
                    self.change({ name: sCode });
                }
            },
        });
    },

    beforeUnmount() {
        onScan.detachFrom(document);
    },

    methods: {
        ...mapActions("plateCreate", [
            "autoAssignConditionToDestinationWell",
            "setActiveCondition",
        ]),
        async onExperimentId(id) {
            this.isLoading = true;
            const protocol = this.protocols.length > 0 ? this.protocols[0].protocol_id : null;
            this.samplesArray = await fetchSamplesByExperiment(id, protocol);
            this.isLoading = false;
            this.setFocus();
        },
        change: _.debounce(function ($event) {
            this.setActiveCondition(this.activeSample);
            if ($event && this.isMultiAssign) {
                this.autoAssignConditionToDestinationWell();
                this.setFocus();
            }
        }, 50),
        setFocus() {
            const inputEl = this.$refs.autocomplete.$el.querySelector("input");
            nextTick(() => {
                if (this.isMultiAssign) {
                    this.$refs.sampleListDiv.focus();
                } else {
                    inputEl.select();
                }
            });
        },
    },

    watch: {
        experimentId: {
            immediate: true,
            handler: async function (newId) {
                await this.onExperimentId(newId);
            },
        },
    },
});
</script>

<style scoped>
.noFocusOutline:focus-visible {
    outline: none;
}
</style>
